import React, { useMemo } from "react";
import { FormControlLabel, Grid, Stack, Switch, Typography } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  FormExists,
  GetFormIsValid,
  GetSectionIsValid,
  SetFieldIsValid,
} from "../../../Redux/Reducers/redux-slice-form-validation";
import {
  BasketShowOnlyAvailable,
  SettingRootField,
  TgopField,
  UpdateItemSettingsImfOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import ConfigCommonSectionFormDelivery from "../ConfigCommon/config-common-section-form-delivery";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ConfigFormIcon from "../ConfigFormIcon";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import { TranskodeGenericOptionsProfile } from "../../../Core/Api/Types/transkode-generic-options-profile";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { TranskodeFieldOrder } from "../../../Core/Api/Types/transkode-field-order";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import { useTranslation } from "react-i18next";

interface ConfigImfProps extends ConfigBaseProps {
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
}

interface TranskodeImfProfile {
  packageId: number;
  isProxy: boolean;
  cpls: TranskodeOptionImf;
}

interface TranskodeOptionImf {
  selectedValue: TranskodeOptionImfValue;
  values: TranskodeOptionImfValue[];
}

interface TranskodeOptionImfValue extends TranskodeGenericOptionsProfile {
  [x: string]: any;
  summary: string;
  duration: string;
}

const ConfigImf: React.FC<ConfigImfProps> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit, handleCancel, handleChangeProduct, item, storages, maillists, ...otherProps } = props;
  const formId = `form-transcodeimf-${item.id}`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const toggleAvailableOnly = useAppSelector(BasketShowOnlyAvailable);
  const dispatch = useAppDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();

  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const settings: TranskodeImfProfile = item.selectedProduct?.settings;
  const productType: OrderProductType = item.selectedProduct?.type!;
  const tgop: TranskodeGenericOptionsProfile = settings.cpls.selectedValue;

  // Update settings
  const handleUpdateParam = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update tgop or asinput product (depending of item type) object into redux
  const handleUpdateTranscodeParam = (field: TgopField, value: any) => {
    dispatch(UpdateItemSettingsImfOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Toggle audio only or video A/V muxed file
  const handleToggleAudioOnly = () => {
    handleUpdateTranscodeParam(TgopField.FileFormat, null);
    handleUpdateTranscodeParam(TgopField.AudioOnly, null);
    dispatch(
      SetFieldIsValid({
        formId: formId,
        formSectionId: "sectionFileSpec",
        fieldId: "fileFormat",
        fieldIsValid: false,
        fieldErrorMessage: t("field:errorMessage.reselectValueAfterToggle"),
      })
    );
  };

  const formValidTrigger = useMemo(() => {
    // In case of invalid, update item status
    if (!formValid && formExists && item.status !== BasketItemStatus.OptionsDone)
      dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return formValid;
  }, [formValid]);

  // Form validator
  const formSectionEditorial = useAppSelector(GetSectionIsValid(formId, "sectionCpl"));
  const formSectionFileSpecs = useAppSelector(GetSectionIsValid(formId, "sectionFileSpec"));

  return (
    <Stack width={"100%"} {...otherProps} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeDcp}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeDcp
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="language" />}
          title={t("common:editorial")}
          status={formSectionEditorial ? "complete" : "warning"}
          statusText={formSectionEditorial ? t("common:configured") : t("common:configurationRequired")}
        >
          <Typography color="text.tier">
            <FormControlLabel
              sx={{ mt: 1.5, ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={settings?.isProxy ?? false}
                  onChange={() => {
                    handleUpdateParam(SettingRootField.IsProxy, !settings?.isProxy);
                  }}
                />
              }
              label={t("field:label.generateProxy")}
            />
          </Typography>
          <Grid container spacing={2}>
            <Grid item md={12}>
              <FormAutocomplete
                label={t("field:label.inputCplLowerCase")}
                fullWidth={true}
                options={settings?.cpls.values ?? []}
                value={settings?.cpls.selectedValue}
                placeholder={t("field:placeholder.selectCplLowerCase")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.ImfCpl, v);
                }}
                getOptionLabel={(value: any) => value.summary}
                isOptionEqualToValue={(option, value) => {
                  return option.assetId! === value.assetId!;
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionCpl",
                  fieldId: "input-cpl",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        {settings.cpls.selectedValue && !settings.isProxy && (
          <FormSection
            icon={<ConfigFormIcon icon="settings" />}
            title={t("form:section.title.technicalSpecs")}
            status={formSectionFileSpecs ? "complete" : "warning"}
            statusText={formSectionFileSpecs ? t("common:configured") : t("common:configurationRequired")}
          >
            <Grid container spacing={3}>
              <Grid item md={6}>
                <FormAutocomplete
                  label={t("field:label.fileFormat")}
                  options={
                    tgop.fileFormat?.values!.filter(
                      (x) =>
                        (!toggleAvailableOnly || x.isAvailable) &&
                        ((!tgop.isAudioOnly && !x.isAudioOnly) || (tgop.isAudioOnly && x.isAudioOnly))
                    )!
                  }
                  value={tgop.fileFormat?.selectedValue}
                  multiple={false}
                  onChange={(v) => {
                    handleUpdateTranscodeParam(TgopField.FileFormat, v);
                  }}
                  getOptionLabel={(value: any) => value.label + (!value.isAvailable ? " (N/A)" : "")}
                  isOptionEqualToValue={(option, value) => option.baseId === value.baseId}
                  filterOptions={(x, s) => x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                  validationProps={{
                    formId: formId,
                    formSectionId: "sectionFileSpec",
                    fieldId: "fileFormat",
                    required: true,
                    initialState: false,
                  }}
                />
              </Grid>

              <Grid item md={3}>
                <FormAutocomplete
                  label={t("common:frameRate")}
                  options={
                    tgop.frameRate?.values!.filter(
                      (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                    )!
                  }
                  value={tgop.frameRate?.selectedValue}
                  multiple={false}
                  onChange={(v) => {
                    if (!v) {
                      handleUpdateTranscodeParam(TgopField.Fps, null);
                      return;
                    }

                    if (
                      tgop.videoFieldOrder?.selectedValue !== null &&
                      tgop.videoFieldOrder?.selectedValue.fieldOrder === TranskodeFieldOrder.Interlaced &&
                      (v.value === 23.98 ||
                        v.value === 24 ||
                        (v.value === 0 &&
                          productType === OrderProductType.TranscodeGenericSingleFile &&
                          (GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value === 23.98 ||
                            GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value === 24)))
                    ) {
                      enqueueCustomSnackbar(t("alert:warning.frameRateIncompatible"), "warning");
                      handleUpdateTranscodeParam(TgopField.Fps, undefined);
                      return;
                    }

                    handleUpdateTranscodeParam(TgopField.Fps, v);
                  }}
                  getOptionLabel={(value: any) =>
                    value.value === 0
                      ? "As Input" +
                        (productType === OrderProductType.TranscodeGenericSingleFile
                          ? " (" + GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value + ")"
                          : "")
                      : value.value.toString()
                  }
                  isOptionEqualToValue={(option, value) => option && option.value === value.value}
                  filterOptions={(x, s) =>
                    x.filter((a) => a.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                  }
                  validationProps={{
                    formId: formId,
                    formSectionId: "sectionFileSpec",
                    fieldId: "fps",
                    required: true,
                    initialState: false,
                  }}
                />
              </Grid>

              <Grid item md={3} display={tgop.isAudioOnly ? "none" : ""}>
                <FormAutocomplete
                  label={t("field:label.scanFieldOrder")}
                  options={tgop.videoFieldOrder?.values!}
                  value={tgop.videoFieldOrder?.selectedValue}
                  multiple={false}
                  onChange={(v) => {
                    if (!v) {
                      handleUpdateTranscodeParam(TgopField.ScanType, null);
                      return;
                    }

                    let srcFps = GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value;
                    let incorrectCfg =
                      tgop.frameRate?.selectedValue?.value === 0 &&
                      productType === OrderProductType.TranscodeGenericSingleFile &&
                      (srcFps === 23.98 || srcFps === 24);

                    if (
                      tgop.frameRate?.selectedValue !== null &&
                      tgop.frameRate?.selectedValue.value !== null &&
                      (tgop.frameRate?.selectedValue.value === 23.98 ||
                        tgop.frameRate?.selectedValue.value === 24 ||
                        incorrectCfg) &&
                      v.fieldOrder === TranskodeFieldOrder.Interlaced
                    ) {
                      enqueueCustomSnackbar(t("alert:warning.scanFieldOrderIncompatible"), "warning");
                      handleUpdateTranscodeParam(TgopField.ScanType, undefined);
                      return;
                    }

                    handleUpdateTranscodeParam(TgopField.ScanType, v);
                  }}
                  onReset={(v) => handleUpdateTranscodeParam(TgopField.ScanType, null)}
                  getOptionLabel={(value: any) => value.fieldOrder.toString()}
                  isOptionEqualToValue={(option, value) => option && option.fieldOrder === value.fieldOrder}
                  filterOptions={(x, s) =>
                    x.filter((a) => a.fieldOrder?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                  }
                  validationProps={{
                    formId: formId,
                    formSectionId: "sectionFileSpec",
                    fieldId: "scanType",
                    required: !tgop.isAudioOnly,
                    initialState: false,
                  }}
                />
              </Grid>
            </Grid>
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0 }}
                control={<Switch size="small" checked={tgop.isAudioOnly} onChange={() => handleToggleAudioOnly()} />}
                label={t("field:label.transcodeToAudioOnlyUpperCase")}
              />
            </Typography>
          </FormSection>
        )}
        <ConfigCommonSectionFormDelivery item={item} formid={formId} storages={storages} maillists={maillists} />
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValidTrigger} />
    </Stack>
  );
};

export default ConfigImf;
